import { pick, uniq, isPlainObject } from 'lodash'
import { captureSentryException } from '@edulastic/common'
import { questionType } from '@edulastic/constants'
import { alignmentStandardsFromUIToMongo } from '../../../assessment/utils/helpers'
import gradesMap from '../../Reports/common/static/json/gradesMap.json'
import { isMathTextFieldEmpty } from '../../questionUtils'

const {
  MULTIPLE_CHOICE,
  ESSAY_RICH_TEXT,
  ESSAY_PLAIN_TEXT,
  CLOZE_DRAG_DROP,
  CLOZE_DROP_DOWN,
  CLOZE_TEXT,
  CLOZE_IMAGE_DRAG_DROP,
  CLOZE_IMAGE_DROP_DOWN,
  CLOZE_IMAGE_TEXT,
  EXPRESSION_MULTIPART,
  EDITING_TASK,
  CHOICE_MATRIX,
  SHORT_TEXT,
  CLASSIFICATION,
  GRAPH,
  AI_QUESTION_ASSISTANCE_ENABLED_QTYPES,
  AI_QUESTION_ASSISTANCE_ENABLED_GRAPH_TYPES,
} = questionType

/**
 * Whether AI question assistance is supported for a given question.
 * Handles graph's per-`graphType`
 * @param {{ type?: string, graphType?: string }} question
 * @returns {boolean}
 */
export const isAIQuestionAssistanceSupported = (question) => {
  if (!question) return false
  const { type, graphType } = question
  if (!AI_QUESTION_ASSISTANCE_ENABLED_QTYPES.includes(type)) return false
  if (type === GRAPH) {
    return AI_QUESTION_ASSISTANCE_ENABLED_GRAPH_TYPES.includes(graphType)
  }
  return true
}

/**
 * Question types that only require stimulus (no options) to generate metadata
 */
const STIMULUS_ONLY_QTYPES = [
  ESSAY_RICH_TEXT,
  ESSAY_PLAIN_TEXT,
  CLOZE_TEXT,
  CLOZE_IMAGE_TEXT,
  SHORT_TEXT,
  GRAPH,
]

/**
 * Builds tooltip items for a standard using curriculum details
 * @param {Object} standard - Standard object with curriculumId and grades
 * @param {Array} allCurriculums - List of all curriculums
 * @returns {Array<{ label: string, value: string }>} - Tooltip items
 */
export const getStandardTooltipItems = (standards, curriculum) => {
  const curriculumName = curriculum?.curriculum || ''
  const subject = curriculum?.subject || ''
  const grades = uniq(
    (standards.flatMap((s) => s.grades || []) || []).filter(Boolean)
  )
    .slice()
    .sort(
      (a, b) =>
        (gradesMap[a]?.sortKey ?? Infinity) -
        (gradesMap[b]?.sortKey ?? Infinity)
    )

  return [
    curriculumName && { label: 'Standard Set', value: curriculumName },
    subject && { label: 'Subject', value: subject },
    grades.length > 0 && { label: 'Grade', value: grades.join(', ') },
  ].filter(Boolean)
}

export const getStandardCurriculumData = (standard, allCurriculums = []) => {
  if (!standard?.curriculumId || !allCurriculums?.length) {
    return {}
  }

  const curriculum = allCurriculums.find(
    (c) => c?._id === standard.curriculumId
  )
  return curriculum
}

export const buildPdlStandardProps = (curriculum, defaultGrades) => {
  const {
    subject,
    pdlCurriculumId,
    pdlAdoptYear,
    pdlCurriculumName = '',
    pdlSubjectLabel = '',
    pdlSubjectId,
    pdlGrades = [],
  } = curriculum || {}

  return {
    pdlCurriculumId,
    pdlAdoptYear,
    pdlCurriculumName,
    pdlSubjectLabel,
    pdlSubjectId,
    pdlGrades,
    grades: defaultGrades,
    subject,
  }
}

const standardFields = [
  '_id',
  'level',
  'grades',
  'identifier',
  'tloIdentifier',
  'tloId',
  'tloDescription',
  'eloId',
  'subEloId',
  'description',
  'curriculumId',
  'pdlId',
]

/**
 * Helper to extract options from object format { responseId: ['Option A', 'Option B'] }
 * @param {Object} optionsObj - Options object
 * @returns {string[]} - Flattened array of option strings
 */
const extractOptionsFromObject = (optionsObj) => {
  if (!isPlainObject(optionsObj)) {
    return []
  }

  return Object.values(optionsObj)
    .flat()
    .filter((opt) => typeof opt === 'string' && opt)
}

const extractOptionsForExpressionMultipart = (question) => {
  const options = extractOptionsFromObject(question.options)
  const validResponse = question?.validation?.validResponse

  if (validResponse) {
    const { dropdown, mathUnits, textinput, value } = validResponse

    if (dropdown && dropdown.value) {
      for (const opt of dropdown.value) {
        if (!opt.value || (opt.value && !opt.value.trim())) {
          return []
        }
      }
    }

    if (mathUnits && mathUnits.value) {
      for (const opt of mathUnits.value) {
        if (isMathTextFieldEmpty(opt.value) || !opt.options.unit) {
          return []
        }
        options.push(opt.value)
      }
    }

    if (textinput && textinput.value) {
      for (const opt of textinput.value) {
        if (!opt.value || (opt.value && !opt.value.trim())) {
          return []
        }
        options.push(opt.value)
      }
    }

    if (value && value.length) {
      for (const opt of value) {
        for (const _opt of opt) {
          if (isMathTextFieldEmpty(_opt.value)) {
            return []
          }
          options.push(_opt.value)
        }
      }
    }
  }

  return options
}

/**
 * Builds the payload for AI metadata generation API based on question type
 * @param {Object} question - Question data object
 * @returns {Object} - { stimulus: string, options: string[] }
 */
export const buildQuestionMetadataPayload = (question) => {
  if (!question) {
    return { stimulus: '', options: [] }
  }

  const { type, stimulus = '' } = question
  let options = []

  switch (type) {
    case MULTIPLE_CHOICE:
      options = (question.options || [])
        .map((opt) => opt?.label)
        .filter(Boolean)
      break

    case CHOICE_MATRIX:
      options = (question.stems || []).filter(Boolean)
      break

    case CLOZE_DRAG_DROP:
      options = (question.options || [])
        .map((opt) => opt?.label)
        .filter(Boolean)
      break

    case CLOZE_DROP_DOWN:
      options = extractOptionsFromObject(question.options)
      break

    case CLOZE_IMAGE_DRAG_DROP:
      options = (question.options || [])
        .map((opt) => opt?.value)
        .filter(Boolean)
      break

    case CLOZE_IMAGE_DROP_DOWN:
      options = (question.options || []).flat().filter(Boolean)
      break

    case EXPRESSION_MULTIPART:
      options = extractOptionsForExpressionMultipart(question)
      break

    case EDITING_TASK:
      options = extractOptionsFromObject(question.options)
      break

    case CLASSIFICATION: {
      const responses = question.groupPossibleResponses
        ? (question.possibleResponseGroups || []).flatMap(
            (g) => g.responses || []
          )
        : question.possibleResponses || []
      options = responses.map((r) => r?.value).filter(Boolean)
      break
    }

    case ESSAY_RICH_TEXT:
    case ESSAY_PLAIN_TEXT:
    case CLOZE_TEXT:
    case CLOZE_IMAGE_TEXT:
    case SHORT_TEXT:
    case GRAPH:
      break

    default:
      break
  }

  return {
    stimulus,
    qType: type,
    ...(options?.length > 0 ? { options } : {}),
  }
}

/**
 * Checks if a question has sufficient content for AI metadata generation
 * based on the question type
 * @param {Object} question - Question data object
 * @returns {boolean} - Whether the question has enough content
 */
export const hasQuestionContentForMetadata = (question) => {
  if (!question) return false

  const { type, stimulus } = question
  const hasStimulusContent =
    typeof stimulus === 'string' && stimulus.trim().length > 0

  if (STIMULUS_ONLY_QTYPES.includes(type)) {
    return hasStimulusContent
  }

  const formattedQuestion = buildQuestionMetadataPayload(question)
  const { options } = formattedQuestion

  const hasOptionsContent = Array.isArray(options) && options.length > 0

  return hasStimulusContent && hasOptionsContent
}

/**
 * @param {Object} params
 * @param {Object} params.suggestedMetaData - Original AI-suggested metadata
 * @param {Object} params.userUpdatedData - User edits that override AI suggestions
 * @returns {{ dok: string|null, difficulty: string|null, standards: Array }}
 */
export const getEffectiveMetadata = ({
  suggestedMetaData = {},
  userUpdatedData = {},
}) => {
  const dok = userUpdatedData.dok || suggestedMetaData?.dok || null
  const difficulty =
    userUpdatedData.difficulty || suggestedMetaData?.difficulty || null
  const standards = userUpdatedData.standards || []

  return { dok, difficulty, standards }
}

/**
 * @param {Array} standards - Selected standards
 * @returns {string|null}
 */
export const getStandardIdsToLog = (standards = []) =>
  (standards || [])
    .map((s) => s._id)
    .filter(Boolean)
    .join(',') || null

export const hasValidStandardSuggestion = (suggestion = {}) =>
  Boolean(suggestion?.standard?._id)

export const getStandardsText = (standards = []) => {
  if (standards.length === 1) {
    const [standard] = standards
    return `${standard?.identifier ?? ''}${
      standard?.description ? ` (${standard.description})` : ''
    }`
  }
  return standards
    .map((standard) => standard?.identifier)
    .filter(Boolean)
    .join(', ')
}

/**
 * @param {Object} suggestedMetaData - Original AI-suggested metadata from state
 * @returns {{ dok: string|null, difficulty: string|null, standardIds: string[] }}
 */
export const getFormattedAISuggestedData = (suggestedMetaData = {}) => {
  const { dok = null, difficulty = null, standards = [] } =
    suggestedMetaData || {}
  const standardIds = standards?.map((s) => s._id).filter(Boolean) || []

  return {
    ...(dok && { dok }),
    ...(difficulty && { difficulty }),
    ...(standardIds?.length > 0 && { standardIds }),
  }
}

/**
 * @param {Object} params - Parameters object
 * @param {Object} params.question - Current question data
 * @param {Object} params.metadata - Resolved metadata { dok, difficulty, standards }
 * @param {Array} params.allCurriculums - List of all curriculums from state
 * @param {Array} params.dictAlignments - Dictionaries alignments with standards in UI format
 * @returns {Object} - Updated question with metadata applied
 */
export const applyAIMetadataToQuestion = ({
  question,
  metadata = {},
  allCurriculums,
  dictAlignments,
}) => {
  const { dok, difficulty, standards = [] } = metadata

  if (!standards || standards.length === 0) {
    return {
      ...question,
      ...(dok && { depthOfKnowledge: dok }),
      ...(difficulty && { authorDifficulty: difficulty }),
    }
  }

  const newStandards = standards.map((s) => pick(s, standardFields))
  const curriculumId = newStandards[0]?.curriculumId
  const curriculum = allCurriculums.find((c) => c._id === curriculumId)

  if (!curriculum) {
    captureSentryException(
      new Error('Curriculum not found for suggested standard'),
      {
        errorMessage: 'AI Question Assistance - METADATA: Curriculum not found',
        standards: newStandards,
        curriculumId,
      }
    )
    return question
  }

  const existingAlignments = (question.alignment || []).filter(
    (a) => !a.isEquivalentStandard
  )

  const existingAlignmentIndex = existingAlignments.findIndex(
    (a) => a.curriculumId === curriculumId
  )

  let updatedAlignments
  let questionGrades = []

  if (existingAlignmentIndex !== -1) {
    const dictAlignment = dictAlignments.find(
      (a) => a.curriculumId === curriculumId
    )
    const existingStandards = dictAlignment?.standards || []

    const mergedStandards = [...existingStandards]
    for (const newStd of newStandards) {
      if (!existingStandards.some((s) => s._id === newStd._id)) {
        mergedStandards.push(newStd)
      }
    }

    questionGrades = uniq(
      mergedStandards.flatMap((s) => s.grades || [])
    ).filter(Boolean)

    const updatedDomains = alignmentStandardsFromUIToMongo(mergedStandards)

    updatedAlignments = existingAlignments.map((alignment, index) =>
      index === existingAlignmentIndex
        ? { ...alignment, grades: [], domains: updatedDomains }
        : alignment
    )
  } else {
    questionGrades = uniq(newStandards.flatMap((s) => s.grades || [])).filter(
      Boolean
    )

    const newQuestionAlignment = {
      curriculum: curriculum.curriculum,
      curriculumId,
      subject: curriculum.subject,
      grades: [],
      domains: alignmentStandardsFromUIToMongo(newStandards),
    }
    updatedAlignments = [...existingAlignments, newQuestionAlignment]
  }

  return {
    ...question,
    alignment: updatedAlignments,
    grades: questionGrades,
    subjects: [curriculum.subject].filter(Boolean),
    ...(dok && { depthOfKnowledge: dok }),
    ...(difficulty && { authorDifficulty: difficulty }),
  }
}

/**
 * Resolves the subject + grades to use for standard suggestions using a per-field precedence:
 * test level -> item level (question's own subjects/grades, then dictionaries alignment) -> user interested.
 * Subjects and grades are resolved independently, so they may come from different levels.
 *
 * @param {string[]} testSubjects - test's subjects
 * @param {string[]} testGrades - test's grades
 * @param {string[]} questionSubjects - currentQuestion.subjects (set when a standard is attached)
 * @param {string[]} questionGrades - currentQuestion.grades (set when a standard is attached)
 * @param {Array} dictionariesAlignments - state.dictionaries.alignments (form state if set by user at test level and no standard attached)
 * @param {string[]} interestedSubjects - user profile interested subjects
 * @param {string[]} interestedGrades - user profile interested grades
 * @returns {{ subjects: string[], grades: string[] }}
 */
export const resolveMetadataContext = ({
  testSubjects = [],
  testGrades = [],
  questionSubjects = [],
  questionGrades = [],
  dictionariesAlignments = [],
  interestedSubjects = [],
  interestedGrades = [],
} = {}) => {
  const dictAlignmentData = dictionariesAlignments?.[0] || {}
  const firstNonEmpty = (...vals) =>
    vals.find((v) => Array.isArray(v) && v.length) || []

  const subjects = firstNonEmpty(
    testSubjects,
    questionSubjects,
    dictAlignmentData.subject ? [dictAlignmentData.subject] : [],
    interestedSubjects
  )
  const grades = firstNonEmpty(
    testGrades,
    questionGrades,
    dictAlignmentData.grades,
    interestedGrades
  )

  return { subjects, grades }
}

/**
 * @param {Array} interestedCurriculums - from getAllInterestedCurriculumsSelector
 * @param {Array} interestedSubjects - from getInterestedSubjectsSelector
 * @returns {{ standardSetOptions: Array }} Options for the standard set dropdown; empty when user has no interested standard sets
 */
export const getStandardSetOptions = ({
  interestedCurriculums,
  interestedSubjects,
}) => {
  try {
    const userInterestedCurriculums = Array.isArray(interestedCurriculums)
      ? interestedCurriculums
      : []
    const userInterestedSubjects = Array.isArray(interestedSubjects)
      ? interestedSubjects
      : []

    if (userInterestedCurriculums.length === 0) {
      return {
        standardSetOptions: [],
      }
    }

    if (userInterestedCurriculums.length === 1) {
      return {
        standardSetOptions: userInterestedCurriculums,
      }
    }

    if (userInterestedSubjects.length === 0) {
      return {
        standardSetOptions: userInterestedCurriculums,
      }
    }

    const _standardSetOptions = userInterestedCurriculums.filter((curriculum) =>
      userInterestedSubjects.includes(curriculum.subject)
    )

    if (_standardSetOptions.length === 1) {
      return {
        standardSetOptions: _standardSetOptions,
      }
    }

    if (_standardSetOptions.length > 1) {
      return {
        standardSetOptions: _standardSetOptions,
      }
    }

    return {
      standardSetOptions: userInterestedCurriculums,
    }
  } catch (error) {
    captureSentryException(error, {
      errorMessage:
        'AI Question Assistance - METADATA: Error getting standard set options for user curriculum input',
      interestedCurriculums,
      interestedSubjects,
    })

    return {
      standardSetOptions: [],
    }
  }
}

/**
 * Builds updated dictionaries alignments for UI sync.
 * @param {Array} standards - Resolved standards array (from resolveEffectiveMetadata)
 * @param {Array} allCurriculums - List of all curriculums from state
 * @param {Array} existingAlignments - Current alignments from dictionaries state
 * @returns {Array} - Updated alignments array
 */
export const buildDictAlignmentFromStandards = (
  standards,
  allCurriculums,
  existingAlignments = []
) => {
  if (!standards?.length) return existingAlignments

  const newStandards = standards.map((s) => pick(s, standardFields))
  const curriculumId = standards[0]?.curriculumId
  const curriculum = allCurriculums.find((c) => c._id === curriculumId)

  if (!curriculum) {
    captureSentryException(
      new Error('Curriculum not found while building alignment'),
      {
        errorMessage:
          'AI Question Assistance - METADATA: Curriculum not found while building alignment',
        standards: newStandards,
        curriculumId,
      }
    )
    return existingAlignments
  }

  const existingAlignmentIndex = (existingAlignments || []).findIndex(
    (a) => a.curriculumId === curriculumId
  )

  if (existingAlignmentIndex !== -1) {
    const existingStandards =
      existingAlignments?.[existingAlignmentIndex]?.standards || []
    const mergedStandards = [...existingStandards]
    for (const newStd of newStandards) {
      if (!existingStandards.some((s) => s._id === newStd._id)) {
        mergedStandards.push(newStd)
      }
    }

    return existingAlignments.map((alignment, index) =>
      index === existingAlignmentIndex
        ? { ...alignment, standards: mergedStandards }
        : alignment
    )
  }

  return [
    ...existingAlignments,
    {
      curriculum: curriculum.curriculum,
      curriculumId,
      subject: curriculum.subject,
      grades: [],
      standards: newStandards,
    },
  ]
}

/**
 * @param {Object} standard - Standard with `_id`
 * @param {Array<{ standard_id: string|number, confidence_score: number }>|undefined} standardIdsWithConfidence - from API `standard_ids`
 * @returns {number|undefined}
 */
export const getConfidenceScoreForStandard = (
  standard,
  standardIdsWithConfidence
) => {
  try {
    if (!standard?._id || !Array.isArray(standardIdsWithConfidence)) {
      throw new Error('Invalid arguments for confidence score')
    }

    const match = standardIdsWithConfidence.find(
      (item) => `${item?.standard_id}` === `${standard._id}`
    )

    if (!match) {
      return undefined
    }
    if (typeof match.confidence_score === 'number') {
      return match.confidence_score
    }
    throw new Error('Invalid confidence score for standard')
  } catch (error) {
    captureSentryException(error, {
      errorMessage:
        'AI Question Assistance - METADATA: Error getting confidence score for standard',
      standard,
      standardIdsWithConfidence,
    })
    return undefined
  }
}

export const isStandardAddedToQuestion = (question = {}) => {
  const alignments = question?.alignment || []
  return alignments.some(
    (alignment) =>
      alignment?.domains?.some((domain) => domain?.standards?.length > 0) ||
      alignment?.standards?.length > 0
  )
}

/**
 * Whether the current user authored the test item (DEC-7).
 * @param {{ createdBy?: { _id?: string } }} item
 * @param {string} userId
 * @returns {boolean}
 */
export const isItemAuthoredByUser = (item, userId) => {
  if (!item || !userId) return false
  return item?.createdBy?._id === userId
}

/**
 * Eligible test items for bulk metadata generation on Test Review.
 * Silently excludes multipart, unsupported qtypes, aligned, and non-owned items.
 * @param {Array} testItems
 * @param {string} userId
 * @returns {Array<{ itemId: string, questionId: string, payload: Object }>}
 */
export const getEligibleTestMetadataQuestions = (testItems = [], userId) =>
  (testItems || []).flatMap((item) => {
    const questions = item?.data?.questions || []
    if (questions.length !== 1) return []
    return questions
      .filter(
        (q) =>
          isAIQuestionAssistanceSupported(q) &&
          !isStandardAddedToQuestion(q) &&
          isItemAuthoredByUser(item, userId)
      )
      .map((q) => ({
        itemId: item._id,
        questionId: q.id,
        payload: buildQuestionMetadataPayload(q),
      }))
  })
